import React, { Suspense } from "react"
import { QueryClient } from "react-query"
import { Navigate, Route, Routes } from "react-router-dom"

import { featureFlags } from "config"
import { useCarealotV2, useNewSummaryView } from "lib/hooks"

import AuthorizedRoute from "./AuthorizedRoute"

import Errand from "components/Errand/Errand"
import PostLogout from "components/navigation/components/PostLogout"
import CreatePasswordPage from "pages/createPassword/CreatePasswordPage"
import DashboardPage from "pages/dashboard/DashboardPage"
import DialNumberPage from "pages/dialNumber/DialNumberPage"
import ErrandPageContainer from "pages/ErrandPage/ErrandPageContainer"
import HelpPage from "pages/help/HelpPage"
import LogIn from "pages/login/Login"
import PhrasePage from "pages/phrases/PhrasesPage"
import SearchPage from "pages/search/SearchPage"
import SearchPageContainer from "pages/search/SearchPageContainer"
import SettingsPage from "pages/settings/SettingsPage"
import UploadPage from "pages/uploadFile/UploadPage"
import DashboardPageV2 from "pages/v2/dashboard/DashboardPage"
import OverviewPage from "pages/v2/overview/OverviewPage"
import PausedPage from "pages/v2/paused/PausedPage"

import LoadingContainer from "./LoadingContainer"
import Localization from "./Localization/Localization"

const BookingPage = React.lazy(() => import("pages/booking/BookingPage"))
const BulkMessagesPage = React.lazy(() => import("pages/bulkMessages/BulkMessagesPage"))
const SearchPageV2 = React.lazy(() => import("pages/search/SearchPageV2"))

interface AppProps {
  queryClient: QueryClient
}

const App = ({ queryClient }: AppProps) => {
  const newVersion = useCarealotV2()
  const newSummaryView = useNewSummaryView()
  return (
    <>
      <Localization>
        <Routes>
          <Route path="/login/itsme" element={<LogIn redirectedFrom="itsme" />} />
          <Route path="/login" element={<LogIn />} />
          <Route path="/:country/:language/create-password" element={<CreatePasswordPage />} />
          <Route path="/post-logout" element={<PostLogout />} />

          <Route
            path="/"
            element={<AuthorizedRoute>{newVersion ? <DashboardPageV2 /> : <DashboardPage />}</AuthorizedRoute>}
          />
          <Route
            path="/overview"
            element={
              <AuthorizedRoute>
                <OverviewPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/paused"
            element={
              <AuthorizedRoute>
                <PausedPage />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <AuthorizedRoute>
                <SearchPageContainer />
              </AuthorizedRoute>
            }>
            <Route index element={newSummaryView ? <SearchPageV2 /> : <SearchPage />} />
            <Route path=":conversationId" element={<Errand />} />
          </Route>
          <Route
            path="/view-errand"
            element={
              <AuthorizedRoute>
                <ErrandPageContainer />
              </AuthorizedRoute>
            }>
            <Route index element={<Navigate to="/" replace />} />
            <Route path=":conversationId" element={<Errand />} />
          </Route>
          {featureFlags.has("sip") && (
            <Route
              path="/dial"
              element={
                <AuthorizedRoute>
                  <DialNumberPage />
                </AuthorizedRoute>
              }
            />
          )}
          <Route
            path="/settings"
            element={
              <AuthorizedRoute>
                <SettingsPage queryClient={queryClient} />
              </AuthorizedRoute>
            }
          />
          {featureFlags.has("phrases") && (
            <Route
              path="/phrases"
              element={
                <AuthorizedRoute>
                  <PhrasePage />
                </AuthorizedRoute>
              }
            />
          )}
          {featureFlags.has("help") && (
            <Route
              path="/help"
              element={
                <AuthorizedRoute>
                  <HelpPage />
                </AuthorizedRoute>
              }
            />
          )}
          {featureFlags.has("booking") && (
            <Route
              path="/booking"
              element={
                <AuthorizedRoute>
                  <Suspense fallback={<LoadingContainer />}>
                    <BookingPage />
                  </Suspense>
                </AuthorizedRoute>
              }
            />
          )}
          {featureFlags.has("bulk_messages") && (
            <Route
              path="/bulk"
              element={
                <AuthorizedRoute>
                  <Suspense fallback={<LoadingContainer />}>
                    <BulkMessagesPage />
                  </Suspense>
                </AuthorizedRoute>
              }
            />
          )}
          <Route
            path="/upload"
            element={
              <AuthorizedRoute>
                <UploadPage />
              </AuthorizedRoute>
            }
          />
        </Routes>
      </Localization>
    </>
  )
}

export default App
